import styled from 'styled-components';

import type { ReactNode } from 'react';

type Props = {
  title: ReactNode;
  /** Body content: plain <p> copy plus any risk-note callouts. */
  children: ReactNode;
  onCancel: () => void;
  onConfirm: () => void;
};

/**
 * Shared Brand Kit delete confirmation on the compact confirm-dialog standard
 * (see docs/design-system.md "Confirmation Dialogs"): pinned width, overlay
 * click cancels, Cancel first and the destructive Delete right-most.
 */
const ConfirmDeleteDialog = (props: Props) => {
  const { title, children, onCancel, onConfirm } = props;
  return (
    <SConfirmOverlay onClick={onCancel}>
      <SConfirmDialog onClick={e => e.stopPropagation()}>
        <SConfirmTitle>{title}</SConfirmTitle>
        <SConfirmBody>{children}</SConfirmBody>
        <SConfirmActions>
          <SConfirmCancelBtn onClick={onCancel}>Cancel</SConfirmCancelBtn>
          <SConfirmDeleteBtn onClick={onConfirm}>Delete</SConfirmDeleteBtn>
        </SConfirmActions>
      </SConfirmDialog>
    </SConfirmOverlay>
  );
};

const SConfirmOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 10000;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const SConfirmDialog = styled.div`
  background: var(--background);
  border: 1px solid var(--border);
  border-radius: 12px;
  padding: 24px;
  /* Compact confirmation-dialog width standard — see docs/design-system.md
     "Confirmation Dialogs". Pinned (min === max) so the alert holds a
     consistent narrow width regardless of title/body length. */
  min-width: var(--dialog-width-confirm);
  max-width: var(--dialog-width-confirm);
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const SConfirmTitle = styled.h3`
  margin: 0;
  color: var(--foreground);
  font-size: var(--text-md);
  font-weight: var(--font-weight-semi-bold);
`;

const SConfirmBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  p {
    margin: 0;
    color: var(--text-secondary);
    font-size: var(--text-sm);
  }
`;

const SConfirmActions = styled.div`
  display: flex;
  gap: 8px;
  justify-content: flex-end;
`;

const SConfirmCancelBtn = styled.button`
  height: var(--btn-h-sm);
  padding: 0 var(--btn-px-sm);
  background: transparent;
  border: 1px solid var(--border);
  border-radius: var(--radius-button);
  color: var(--foreground);
  font-size: var(--text-xs);
  font-weight: var(--font-weight-medium);
  cursor: pointer;
  transition: var(--transition-normal);
  &:hover {
    background: var(--surface-elevated);
    border-color: var(--text-muted);
  }
`;

const SConfirmDeleteBtn = styled.button`
  height: var(--btn-h-sm);
  padding: 0 var(--btn-px-sm);
  background: var(--destructive, #ef4444);
  border: none;
  border-radius: var(--radius-button);
  color: #fff;
  font-size: var(--text-xs);
  font-weight: var(--font-weight-semi-bold);
  cursor: pointer;
  transition: var(--transition-normal);
  &:hover {
    opacity: 0.85;
  }
`;

export default ConfirmDeleteDialog;
